import Config from 'config/settings';
import { globals } from 'config/globals';
import { device } from 'utils/device';
import { Simple } from 'utils/analytics';
import HomeColours from 'app/views/home/config/HomeColours';
import ParticleGridPatterns from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import { OptimisedSingleLine } from 'app/views/home/webgl/particlesOptimised/departments/systems/OptimisedSingleLine';

import type DepartmentColours from 'app/views/home/webgl/particlesOptimised/departments/DepartmentColours';
import type { SystemsContext } from 'app/views/home/webgl/particlesOptimised/departments/systems/SystemsContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * LinesPhase — the systems department's SINGLE phase: FORK of
 * SystemsDepartment._singleLine + _tickLineMode/_setLineMode/
 * _tickMarkerColour + the per-frame line/trail passes.
 *
 * VISUAL: up to ~11 travelling dots ("heads") glide between points of the
 * systems dot grid leaving dotted rails behind them (the trail lanes),
 * dropping breathing endpoint markers where they turn; the safe zone at the
 * grid centre dims heads and mutes their dots; every ~3s the accent colour
 * rotates through the greenBlues set (markers + the big systems items), and
 * an 8-entry mode table cycles the retargeting style (45-degree vs
 * right-angle turns, single-line mode) over live lines.
 *
 * WHY ONE PHASE (the owner's uniformity question, answered from the
 * reference): the reference stage table is [SingleLine x10000 frames] — a
 * single stage looping onto itself. Its internal cycles (_lineModeSettings,
 * the marker colour rotation) mutate GLOBALS that in-flight lines consume
 * when they next retarget: lines PERSIST across mode changes, there is no
 * arm/teardown per mode, and a phase transition (hide + start) would tear
 * lines down where the reference explicitly keeps them. So the mode cycle
 * is internal variation inside one phase, not distinct phases. The stage
 * WRAP (every 10000 counter ticks) re-runs start() — which is exactly the
 * reference behaviour: _singleLine re-arms, topping lines up to the >10
 * cap and resetting fastModeScale/lineMode/accent, over the surviving
 * lines. hide() therefore deliberately does NOT clear lines (they persist
 * across the wrap); reset() — the scene-stop path — does.
 *
 * The reference's Rain/BasicGrid dispatch arms are UNREACHABLE (dead,
 * documented-not-ported); that also covers the physics _processTarget
 * machinery (physTargetSlot/targetsPx), whose only feeder is the dead Rain
 * stage — kernel path parity-proven and ready, unfed on the live flow.
 */
export class LinesPhase implements DepartmentPhase {
	public readonly name = 'SingleLine';

	private _ctx: SystemsContext;
	private _colours: DepartmentColours;
	private _getFirstColour: () => string;

	private _lines: Array<OptimisedSingleLine> = [];

	private _lineModeIndex = 0;
	private _lineModeCounter = 0;
	private _lineModeChangeAtCount = 0;

	private _colourModeCount = 0;
	private _colourModeChangeAtCount = 3000 / 16;

	private _totalPasses = 0;

	private _lineModeSettings = [
		{ rightAngle: false, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: false, singleLineMode: true, timeMS: 5000 },
		{ rightAngle: true, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: true, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: false, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: false, singleLineMode: false, timeMS: 15000 },
		{ rightAngle: true, singleLineMode: true, timeMS: 10000 },
		{ rightAngle: true, singleLineMode: false, timeMS: 10000 }
	];

	constructor(ctx: SystemsContext, colours: DepartmentColours, getFirstColour: () => string) {
		this._ctx = ctx;
		this._colours = colours;
		this._getFirstColour = getFirstColour;
	}

	public init(): void {
		//No one-time setup — the grid tables live in the reused
		//ParticleGridTargets singleton.
	}

	/** _singleLine, verbatim: spawn lines off the dot grid (capped at >10
	 *  across wraps), reset the mode/speed cycles, re-assert the accent. */
	public start(): void {
		globals.singleLineMode = false;
		globals.rightAngleMode = false;

		const totalLines = device.innerWidth < 600 ? 8 : 10;
		let delay = 0;

		for (let i = 0; i < totalLines; i++) {
			if (this._lines.length > 10) {
				return;
			}

			const spawn = ParticleGridPatterns.getSpawnTarget();

			let end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				end = ParticleGridPatterns.get45DegreeTarget(spawn.col, spawn.row, 8);
			}

			if (!end) {
				continue;
			}

			const line = new OptimisedSingleLine(this._ctx, `grid_${spawn.col}_${spawn.row}`, `grid_${end.col}_${end.row}`, 1, 1, 40000, delay);

			line.endTargetGridPoint = end;

			this._lines.push(line);

			delay += 100;
		}

		globals.fastModeScale = 0.8;

		globals.fastModeScale = 0.6;
		const tweenOb = { value: globals.fastModeScale };
		gsap.TweenMax.to(tweenOb, 5, {
			value: 0.95,
			ease: Config.linearEase,
			onUpdate: () => {
				globals.fastModeScale = tweenOb.value;
			}
		});

		this._lineModeIndex = 0;
		this._lineModeCounter = 0;
		this._setLineMode();
		this._lineModeIndex = 1;

		globals.accentColour = this._getFirstColour();
		this._colours.changeSystemsColour([globals.accentColour], true);
	}

	public tick(): void {
		this._tickLineMode();
		this._tickMarkerColour();

		for (let i = 0; i < this._lines.length; i++) {
			this._lines[i].tick();
		}

		//The trail fade pass (ParticleTrail.tick over the trail lanes):
		this._ctx.tickTrails();
	}

	public hide(): void {
		//Deliberately NOTHING here: on the stage wrap the reference keeps
		//every live line (see the class header) — clearing belongs to the
		//scene stop (reset()).
	}

	public reset(): void {
		//stopAsActiveScene's _clearAllLines:
		for (let i = 0; i < this._lines.length; i++) {
			this._lines[i].clear();
		}

		this._lines = [];
		//(fastModeScale / the mode cursors persist across stops like the
		//reference fields — only start() resets them.)
	}

	/** The facade's lerp-completion routing: heads are the only systems
	 *  lanes that lerp; the owning line consumes the event. */
	public onPhysicsTargetReached(lane: number): void {
		for (let i = 0; i < this._lines.length; i++) {
			if (this._lines[i].headLane === lane) {
				this._lines[i].headTargetReached();
				return;
			}
		}
	}

	//Private — the configuration cycles (internal variation, see header)
	//-----------------------------------------------------------------------------------------------

	private _tickLineMode(): void {
		this._lineModeCounter += this._ctx.deps.context.deltaTime;

		if (this._lineModeCounter >= this._lineModeChangeAtCount) {
			this._lineModeIndex++;
			if (this._lineModeIndex >= this._lineModeSettings.length) {
				this._totalPasses++;
				Simple.event('home_systems_full_phase_complete', { total_passes: this._totalPasses });
				this._lineModeIndex = 0;
			}

			Simple.event('home_systems_next_phase', { index: this._lineModeIndex });

			this._lineModeCounter = 0;

			//Index 0 skips the settings write — settings[0] is only ever
			//applied by start()'s first pass (reference quirk, kept):
			if (this._lineModeIndex) {
				this._setLineMode();
			}
		}
	}

	private _setLineMode(): void {
		globals.singleLineMode = this._lineModeSettings[this._lineModeIndex].singleLineMode;
		globals.rightAngleMode = this._lineModeSettings[this._lineModeIndex].rightAngle;
		this._lineModeChangeAtCount = this._lineModeSettings[this._lineModeIndex].timeMS
			? this._lineModeSettings[this._lineModeIndex].timeMS / 16
			: 10000 / 16;

		this._lineModeChangeAtCount *= 0.8;
	}

	private _tickMarkerColour(): void {
		this._colourModeCount += this._ctx.deps.context.deltaTime;

		if (this._colourModeCount >= this._colourModeChangeAtCount) {
			globals.accentColour = HomeColours.getRandomColour(HomeColours.greenBlues);

			this._colours.changeSystemsColour([globals.accentColour], false);

			this._colourModeCount = 0;
		}
	}
}
